import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Card } from './ui/card';

const CashFlowChart = ({ data, year }) => {
  if (!data || data.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center text-slate-500">Nessun dato disponibile per il grafico</div>
      </Card>
    );
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const monthData = data.find(d => d.month_name === label);
      
      return (
        <div className="bg-white p-4 border border-slate-200 rounded-lg shadow-lg">
          <p className="font-semibold text-slate-900 mb-2">{label}</p>
          {monthData && monthData.is_future && (
            <p className="text-xs text-blue-600 mb-2">📊 Previsione</p>
          )}
          {monthData && monthData.is_current && (
            <p className="text-xs text-green-600 mb-2">📅 Mese Corrente</p>
          )}
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 mb-1">
              <span className="text-sm" style={{ color: entry.color }}>
                {entry.name}:
              </span>
              <span className="font-semibold" style={{ color: entry.color }}>
                €{entry.value?.toFixed(2)}
              </span>
            </div>
          ))}
          {monthData && (
            <div className="mt-2 pt-2 border-t border-slate-200">
              <div className="text-xs text-slate-600">
                Riserva Cassa: €{monthData.riserva_cassa?.toFixed(2)}
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="p-6 border-0 shadow-lg">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-slate-900">
          📈 Analisi Cash Flow {year}
        </h3>
        <p className="text-sm text-slate-600">
          Confronto tra fabbisogno, entrate, disponibilità e storico anno precedente
        </p>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis 
            dataKey="month_name" 
            tick={{ fontSize: 12 }}
            stroke="#64748b"
          />
          <YAxis 
            tick={{ fontSize: 12 }}
            stroke="#64748b"
            tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
          />
          
          {/* Reference line at 0 */}
          <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
          
          {/* Lines */}
          <Line 
            type="monotone" 
            dataKey="fabbisogno" 
            stroke="#ef4444" 
            strokeWidth={2}
            name="Fabbisogno (Uscite)"
            dot={{ fill: '#ef4444', r: 4 }}
            activeDot={{ r: 6 }}
          />
          
          <Line 
            type="monotone" 
            dataKey="entrate" 
            stroke="#10b981" 
            strokeWidth={2}
            name="Entrate"
            dot={{ fill: '#10b981', r: 4 }}
            activeDot={{ r: 6 }}
          />
          
          <Line 
            type="monotone" 
            dataKey="disponibilita" 
            stroke="#3b82f6" 
            strokeWidth={3}
            name="Disponibilità (Entrate + Riserva - Uscite)"
            dot={{ fill: '#3b82f6', r: 5 }}
            activeDot={{ r: 7 }}
          />
          
          <Line 
            type="monotone" 
            dataKey="storico_entrate" 
            stroke="#a855f7" 
            strokeWidth={2}
            strokeDasharray="5 5"
            name="Storico Entrate (Anno Precedente)"
            dot={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Legend Info */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <div className="flex items-center gap-2 p-2 bg-red-50 rounded">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
          <span className="text-slate-700"><strong>Fabbisogno:</strong> Uscite mensili (fatture passive + riconciliazioni)</span>
        </div>
        <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span className="text-slate-700"><strong>Entrate:</strong> Bookings + Fatture attive + Riconciliazioni</span>
        </div>
        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
          <span className="text-slate-700"><strong>Disponibilità:</strong> Liquidità totale dopo movimenti</span>
        </div>
        <div className="flex items-center gap-2 p-2 bg-purple-50 rounded">
          <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
          <span className="text-slate-700"><strong>Storico:</strong> Dati anno precedente per confronto</span>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-900">
          <strong>ℹ️ Previsioni:</strong> I mesi futuri mostrano previsioni calcolate sulla media storica degli ultimi 2-3 anni con fattori di crescita applicati (10% entrate, 5% uscite).
        </p>
      </div>
    </Card>
  );
};

export default CashFlowChart;
